import React, { useMemo, useRef } from 'react';
import { Upload, FileText, Image as ImageIcon, ChevronLeft, ChevronRight, PlusSquare } from 'lucide-react';

const WorksheetUploadTray = ({
  expanded,
  onToggleExpanded,
  uploads,
  selectedAssetId,
  selectedPdfPage,
  onSelectAsset,
  onChangePdfPage,
  onPasteSelected,
  onInsertAsPages,
  onUploadFiles,
  notebookMode = false,
  isProcessing,
  uploadError,
}) => {
  const fileInputRef = useRef(null);

  const selectedAsset = useMemo(
    () => uploads.find((asset) => asset.id === selectedAssetId) || null,
    [selectedAssetId, uploads]
  );

  const selectedPage = useMemo(() => {
    if (!selectedAsset) return null;
    if (selectedAsset.type === 'image') return selectedAsset.pages?.[0] || null;
    return (selectedAsset.pages || []).find((p) => p.pageNumber === selectedPdfPage) || selectedAsset.pages?.[0] || null;
  }, [selectedAsset, selectedPdfPage]);

  return (
    <div className={`wb-upload-tray ${expanded ? 'expanded' : ''}`}>
      <button className="wb-upload-tray__toggle" onClick={onToggleExpanded} title="Uploads">
        <Upload size={16} />
        <span>Uploads</span>
      </button>

      {expanded && (
        <div className="wb-upload-tray__panel">
          <div className="wb-upload-tray__actions">
            <button
              className="wb-upload-tray__add"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
            >
              <Upload size={14} />
              <span>{isProcessing ? 'Processing...' : 'Add PDF/Image'}</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/jpg"
              multiple
              hidden
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) {
                  onUploadFiles(files);
                }
                e.target.value = '';
              }}
            />
          </div>

          {uploadError && <div className="wb-upload-tray__error">{uploadError}</div>}

          <div className="wb-upload-tray__list">
            {uploads.length === 0 && (
              <div className="wb-upload-tray__empty">Upload a worksheet PDF or image</div>
            )}
            {uploads.map((asset) => (
              <button
                key={asset.id}
                className={`wb-upload-tray__item ${selectedAssetId === asset.id ? 'active' : ''}`}
                onClick={() => onSelectAsset(asset.id)}
              >
                {asset.type === 'pdf' ? <FileText size={14} /> : <ImageIcon size={14} />}
                <span>{asset.name}</span>
              </button>
            ))}
          </div>

          {selectedAsset && selectedPage && (
            <div className="wb-upload-tray__preview">
              <img src={selectedPage.src} alt="Selected worksheet preview" />
              {selectedAsset.type === 'pdf' && (
                <div className="wb-upload-tray__pager">
                  <button
                    onClick={() => onChangePdfPage(Math.max(1, selectedPdfPage - 1))}
                    disabled={selectedPdfPage <= 1}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span>Page {selectedPdfPage} / {selectedAsset.pages.length}</span>
                  <button
                    onClick={() => onChangePdfPage(Math.min(selectedAsset.pages.length, selectedPdfPage + 1))}
                    disabled={selectedPdfPage >= selectedAsset.pages.length}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
              <button className="wb-upload-tray__paste" onClick={onPasteSelected}>
                <PlusSquare size={14} />
                <span>{notebookMode ? 'Add Current Page' : 'Paste to Whiteboard'}</span>
              </button>
              <button className="wb-upload-tray__paste" onClick={onInsertAsPages}>
                <FileText size={14} />
                <span>{selectedAsset.type === 'pdf' ? 'Insert PDF as Pages' : 'Insert as Page'}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorksheetUploadTray;
